import * as Arr from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';
import * as Monoid from 'fp-ts/Monoid';
import * as O from 'fp-ts/Option';
import * as Rec from 'fp-ts/Record';
import { iso } from 'newtype-ts';
import * as Product from '../product';
import { propertySemigroup } from './configurableProperties/property';
import { fromApiProductProperty } from './configurableProperties/propertyValues';
export * as PropertyValues from './configurableProperties/propertyValues';
/** Concatinate _ConfigurableProperties by combining Properties when
 * two of the same ConfigProperties keys are changed. In this case, Properties will be merged
 * by keeping the first displayName and by merging the propertyValues objects.
 *
 * This is a 'deep' operation - it will merge the whole structure, while simply using
 * Object.assign will only merge top-level keys.
 *
 * { A: propOne } combined with { A: propTwo, B: propThree }
 * will result in { A: propertySemigroup.concat(propOne, PropTwo), B: propThree }
 */
export const _configurablePropertiesMonoid = 
/**
 * NOTE: The only difference between a Semigroup and a Monoid is that a Monoid
 * has an `empty` value. This can be used, for example, to concatinate a full list
 * of _ConfigurableProperties together and safely handle the empty list case.
 *
 * The empty value of a Record's union monoid is an empty object: {}.
 */
Rec.getUnionMonoid(propertySemigroup);
const _ = iso();
export const empty = () => _.wrap({});
export const fromApiConfiguratorDefinition = (cd) => {
    return _.wrap(cd.configurableProperties);
};
export const fromProductVariants = (variants) => {
    return pipe(variants, Arr.chain((variant) => pipe(Object.entries(Product.configurableProperties(variant) ?? {}), Arr.map(([propertyKey, configurablePropertyInfo]) => {
        const propertyValue = pipe(fromApiProductProperty(propertyKey, Product.properties(variant)), O.map(propertyValue => ({ [propertyValue.displayText]: propertyValue })), O.getOrElse(() => ({})));
        return {
            [propertyKey]: {
                displayName: configurablePropertyInfo.label,
                propertyValues: propertyValue,
            },
        };
    }))), 
    // Concatinate all ConfigurableProperties from all Variants using the rules defined in the Monoid
    Monoid.concatAll(_configurablePropertiesMonoid), _.wrap);
};
export const property = (propertyName, configurableProperties) => {
    return pipe(configurableProperties, _.unwrap, c => c[propertyName], O.fromNullable);
};
export const isAlwaysDisplayProperty = (propertyName, configurableProperties) => {
    return pipe(configurableProperties, _.unwrap, c => Object.keys(c[propertyName]).includes('alwaysDisplay'));
};
export const propertyNames = (configurableProperties) => {
    return pipe(configurableProperties, _.unwrap, Object.keys);
};
